import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import React from "react";

type Props = {
  open: boolean;
  title: string;
  /** What would happen, as discrete items — never a paragraph the reader has to parse. */
  items: string[];
  /** Anything worth saying beneath the list, such as an indicative turnaround. */
  note?: string | null;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * The shared cross-system action modal (S7.7, story 5633).
 *
 * Every action that reaches into another system — a HealthTick review request, the SOP email, an
 * Imperans push — opens this first, listing what would happen as discrete items **before anything is
 * written**. Cancel leaves the record untouched, and an explicit confirm is always required. The
 * pattern is identical whether the integration behind it is live or still a placeholder, so nobody
 * ever believes an email was sent when it wasn't — and a browser alert is never used for this.
 *
 * Styling follows the DESIGN.md ActionModal treatment: centred, 520px at most, elevation 8, scrim.
 */
const ActionModal: React.FC<Props> = ({
  open,
  title,
  items,
  note,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  busy,
  onConfirm,
  onCancel,
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth={false}
    PaperProps={{ elevation: 8, sx: { maxWidth: 520, width: "100%" } }}
  >
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="text.secondary">
        Confirming this will:
      </Typography>
      <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
        {items.map((item) => (
          <li key={item}>
            <Typography variant="body2">{item}</Typography>
          </li>
        ))}
      </Box>
      {note && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          {note}
        </Typography>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel}>{cancelLabel}</Button>
      <Button variant="contained" disabled={busy} onClick={onConfirm}>
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export default ActionModal;
